import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ConversationList } from "@/components/conversation";
import { ConversationDetails } from "@/components/conversation/ConversationDetails";
import { ConversationViewToggle } from "@/components/conversation/ConversationViewToggle";
import { InboxFilterBar } from "@/components/conversation/InboxFilterBar";
import { INBOX_QUEUE_EMPTY } from "@/components/conversation/InboxQueueTabs";
import { InboxViewChips } from "@/components/conversation/InboxViewChips";
import { isInboxViewSorting } from "@/lib/inboxView";
import { extractConversationSignals } from "@/components/conversation/conversationListShared";
import { DEFAULT_CONVERSATION_TIME_RANGE } from "@/constants/conversationFilters";
import { useAgentsList } from "@/hooks/agent/useAgents";
import { useConversations, useMarkAllViewed } from "@/hooks/conversation/useConversations";
import { useInboxFilters, useSaveInboxFilters } from "@/hooks/conversation/useInboxFilters";
import { useInboxViews } from "@/hooks/inboxView/useInboxViews";
import { supabase } from "@/integrations/supabase/client";
import { Agent } from "@/types/agent/agent";
import {
  Conversation,
  ConversationChannel,
  ConversationsPageProps,
  ConversationViewMode,
  InboxFilters,
  InboxQueue,
} from "@/types/conversation/conversation";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const VIEW_MODE_STORAGE_KEY = "hg.conversations.view-mode";

const VIEW_FILTER_EMPTY = {
  title: "Nothing in this view yet",
  description: "Conversations your genies match to this view show up here. Try a longer time range or another view.",
};

const VIEW_SORTING_EMPTY = {
  title: "Your genie is sorting your conversations",
  description: "It's reading each conversation and adding the ones that fit this view. They appear here as it goes.",
};

const readStoredViewMode = (): ConversationViewMode => {
  if (typeof window === "undefined") return "table";
  const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  return stored === "rows" || stored === "table" || stored === "cards" ? stored : "table";
};

export default function ConversationsPage({
  embedded = false,
  onConversationsChange,
}: ConversationsPageProps = {}) {
  const { data: agents = [] as Agent[], isLoading: agentsLoading } = useAgentsList();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once on mount — this page has no other URL-driven filter state to
  // reconcile with, so a digest-email deep link (?filter=needs_attention&date=today)
  // only needs to seed the initial view, not stay in sync afterwards.
  const [initialUrlFilter] = useState(() => searchParams.get("filter"));
  const [urlTimeRange] = useState(() => (searchParams.get("date") === "today" ? "24h" : null));
  const [urlQueue] = useState<InboxQueue | null>(() => {
    const value = searchParams.get("queue");
    return value === "attention" || value === "mine" || value === "handled" ? value : null;
  });
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<ConversationChannel | null>(null);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>(urlTimeRange ?? DEFAULT_CONVERSATION_TIME_RANGE);
  const [viewMode, setViewMode] = useState<ConversationViewMode>(readStoredViewMode);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [queue, setQueue] = useState<InboxQueue>(urlQueue ?? "all");

  // Each user lands on the filters they last used; a deep link's date or queue
  // still wins over the remembered one.
  const { data: savedFilters, isPending: savedFiltersPending } = useInboxFilters();
  const { mutate: saveFilters } = useSaveInboxFilters();
  const [filtersRestored, setFiltersRestored] = useState(false);

  useEffect(() => {
    if (filtersRestored || savedFiltersPending) return;
    if (savedFilters) {
      setSelectedAgent(savedFilters.agentId);
      setSelectedChannel(savedFilters.channel);
      setSelectedViewId(savedFilters.viewId);
      if (!urlTimeRange && savedFilters.timeRange) setTimeRange(savedFilters.timeRange);
      if (!urlQueue && savedFilters.queue) setQueue(savedFilters.queue);
    }
    setFiltersRestored(true);
  }, [filtersRestored, savedFiltersPending, savedFilters, urlTimeRange, urlQueue]);

  const { data: viewsData, isLoading: viewsLoading, refetch: refetchViews } = useInboxViews();
  const views = useMemo(() => viewsData?.views ?? [], [viewsData]);

  const handleViewModeChange = useCallback((mode: ConversationViewMode) => {
    setViewMode(mode);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // localStorage unavailable — preference is in-memory only this session.
    }
  }, []);

  // The queues are over a Listener genie's calls and forwarded email.
  const hasListener = useMemo(() => agents.some((agent) => agent.type === "listener"), [agents]);
  const activeQueue: InboxQueue = hasListener ? queue : "all";

  // A remembered genie or view that has since been deleted reads as "all".
  const activeAgent = selectedAgent && agents.some((a) => a.id === selectedAgent) ? selectedAgent : null;
  const activeViewId = selectedViewId && views.some((v) => v.id === selectedViewId) ? selectedViewId : null;

  const agentIds = useMemo(() => {
    if (!activeAgent) return agents.map((a) => a.id);
    return [activeAgent];
  }, [activeAgent, agents]);

  // Wait for the genie list: asking with an empty list would fetch once for
  // "no genies" and again a moment later for the real ones. The remembered
  // filters (and the views they may name) are waited for the same way.
  const {
    data: conversationData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useConversations(
    agentIds,
    timeRange,
    true,
    { queue: activeQueue, channel: selectedChannel ?? undefined, viewId: activeViewId ?? undefined },
    { enabled: !agentsLoading && filtersRestored && !(selectedViewId && viewsLoading), withCounts: true },
  );

  const rememberFilters = (patch: Partial<InboxFilters>) => {
    saveFilters({
      agentId: activeAgent,
      channel: selectedChannel,
      viewId: activeViewId,
      timeRange,
      queue: queue === "all" ? null : queue,
      ...patch,
    });
  };

  const handleAgentChange = (agentId: string | null) => {
    setSelectedAgent(agentId);
    rememberFilters({ agentId });
  };

  const handleChannelChange = (channel: ConversationChannel | null) => {
    setSelectedChannel(channel);
    rememberFilters({ channel });
  };

  const handleViewChange = (viewId: string | null) => {
    setSelectedViewId(viewId);
    rememberFilters({ viewId });
  };

  const handleTimeRangeChange = (range: string) => {
    setTimeRange(range);
    rememberFilters({ timeRange: range });
  };

  const handleQueueChange = (next: InboxQueue) => {
    setQueue(next);
    rememberFilters({ queue: next === "all" ? null : next });
  };

  const handleClearPanelFilters = () => {
    setSelectedAgent(null);
    setSelectedChannel(null);
    setTimeRange(DEFAULT_CONVERSATION_TIME_RANGE);
    rememberFilters({ agentId: null, channel: null, timeRange: DEFAULT_CONVERSATION_TIME_RANGE });
  };

  const handleManageViews = () => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "views");
    setSearchParams(next, { replace: true });
  };

  const conversations = useMemo(() => {
    if (!conversationData?.pages) return [];
    return conversationData.pages.flatMap((page) => page.data);
  }, [conversationData]);
  const displayConversations = useMemo(() => {
    if (initialUrlFilter !== "needs_attention") return conversations;
    return conversations.filter((c) => extractConversationSignals(c).attentionBadge !== null);
  }, [conversations, initialUrlFilter]);

  useEffect(() => {
    onConversationsChange?.(displayConversations);
  }, [displayConversations, onConversationsChange]);

  // Conversations are not requested until the genie list is in, so that wait
  // counts as loading too; otherwise the empty state would flash first.
  const displayConversationsLoading =
    isLoading || agentsLoading || !filtersRestored || Boolean(selectedViewId && viewsLoading);

  const queryClient = useQueryClient();
  const { mutateAsync: markAllViewed } = useMarkAllViewed();

  useEffect(() => {
    const channel = supabase.channel("conversations");

    const subscription = channel
      .on("broadcast", { event: "recorded" }, () => {
        queryClient.invalidateQueries({ queryKey: ["conversations"] });
      })
      .on("broadcast", { event: "completed" }, () => {
        queryClient.invalidateQueries({ queryKey: ["conversations"] });
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  // While the genie is still sorting conversations into the selected view,
  // keep its rows (and so this list) filling in rather than waiting for a reload.
  const selectedViewSorting = isInboxViewSorting(views.find((v) => v.id === activeViewId));
  const [wasSorting, setWasSorting] = useState(false);
  useEffect(() => {
    if (!selectedViewSorting) {
      if (wasSorting) {
        setWasSorting(false);
        queryClient.invalidateQueries({ queryKey: ["conversations"] });
      }
      return;
    }
    setWasSorting(true);
    const timer = window.setInterval(() => {
      refetchViews();
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [selectedViewSorting, wasSorting, refetchViews, queryClient]);

  const handleMarkAllAsReviewed = async () => {
    try {
      const result = await markAllViewed();
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      window.dispatchEvent(new Event("conversationViewed"));
      toast.success(`Marked ${result.updatedCount} conversations as viewed`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to mark conversations as viewed");
    }
  };

  const handleViewDetails = useCallback(
    (conversation: Conversation) => {
      setSelectedConversation(conversation);
    },
    [],
  );

  // Exact totals for the current filters come from the server. A digest link's
  // needs-attention filter only covers what is loaded, so it counts the rows on screen.
  const serverTotals = initialUrlFilter !== "needs_attention" ? conversationData?.pages[0] : undefined;
  const conversationCount = serverTotals?.total ?? displayConversations.length;
  const unreviewedCount = useMemo(
    () => serverTotals?.unreviewed ?? displayConversations.filter((c) => !c.viewed).length,
    [serverTotals, displayConversations],
  );
  const countIsPartial = serverTotals?.total === undefined && hasNextPage;

  const body = agentsLoading ? (
    <div className="text-center py-16">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-genie-primary mb-4"></div>
      <p className="text-gray-600">Loading your genies...</p>
    </div>
  ) : agents.length === 0 ? (
    <div className="text-center py-16">
      <h2 className="text-xl font-semibold text-gray-800 mb-2">No Genies Found</h2>
      <p className="text-gray-600 mb-6">You need to create a genie first to view conversations.</p>
      <a
        href="/user-hub/genie-setup"
        className="inline-flex items-center px-4 py-2 rounded-xl text-white font-semibold shadow-lg shadow-genie-primary/30 hover:opacity-90 transition-all"
        style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
      >
        Create Your First Genie
      </a>
    </div>
  ) : (
    <>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-[#222B45]">Recent Conversations</h2>
          <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-sm text-gray-500">
            {conversationCount > 0 ? (
              <>
                <span>
                  {conversationCount.toLocaleString()}
                  {countIsPartial ? "+" : ""} conversation{conversationCount !== 1 ? "s" : ""}
                </span>
                {unreviewedCount > 0 && (
                  <>
                    <span aria-hidden>·</span>
                    <span>{unreviewedCount} unreviewed</span>
                    <span aria-hidden>·</span>
                    <button
                      type="button"
                      onClick={handleMarkAllAsReviewed}
                      className="-my-3 py-3 sm:my-0 sm:py-0 text-sm text-genie-primary hover:underline"
                    >
                      Mark all reviewed
                    </button>
                  </>
                )}
              </>
            ) : (
              "Your genie interactions at a glance"
            )}
          </p>
        </div>
        <div className="shrink-0">
          <ConversationViewToggle value={viewMode} onChange={handleViewModeChange} />
        </div>
      </div>

      <div className="mb-4">
        <InboxFilterBar
          showQueue={hasListener}
          queue={queue}
          onQueueChange={handleQueueChange}
          agents={agents}
          agentId={activeAgent}
          onAgentChange={handleAgentChange}
          channel={selectedChannel}
          onChannelChange={handleChannelChange}
          timeRange={timeRange}
          onTimeRangeChange={handleTimeRangeChange}
          onClear={handleClearPanelFilters}
        />
      </div>

      {!viewsLoading && (
        <div className="mb-4">
          <InboxViewChips
            views={views}
            value={activeViewId}
            onChange={handleViewChange}
            agentId={activeAgent}
            onManage={embedded ? handleManageViews : undefined}
          />
        </div>
      )}

      <ConversationList
        conversations={displayConversations}
        isLoading={displayConversationsLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onViewDetails={handleViewDetails}
        onMarkAllAsReviewed={handleMarkAllAsReviewed}
        onLoadMore={fetchNextPage}
        emptyMessage={
          activeQueue !== "all"
            ? INBOX_QUEUE_EMPTY[activeQueue]
            : activeViewId
              ? selectedViewSorting
                ? VIEW_SORTING_EMPTY
                : VIEW_FILTER_EMPTY
              : undefined
        }
      />
    </>
  );

  if (embedded) {
    return (
      <>
        {body}
        {selectedConversation && (
          <ConversationDetails
            conversation={selectedConversation}
            isOpen
            onClose={() => setSelectedConversation(null)}
          />
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-8 max-w-[1920px] w-full">
        <div className="flex items-center mb-8">
          <GenieIcon name="chat" size={28} label="Conversations" className="h-7 w-7 md:h-8 md:w-8 mr-3 sm:mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-genie-text-primary">My Conversations</h1>
            <p className="text-genie-text-sub mt-1">Track and analyze your genie interactions</p>
          </div>
        </div>

        {body}
        {selectedConversation && (
          <ConversationDetails
            conversation={selectedConversation}
            isOpen
            onClose={() => setSelectedConversation(null)}
          />
        )}
      </div>
    </div>
  );
}
